import dynamic from "next/dynamic";
import Head from "next/head";
import React, { useEffect, useState } from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import type { PublicMap } from "@/funnel/publicMap";
import type { FunnelFlow } from "@/funnel/types";

const PublicMapViewer = dynamic(() => import("@/funnel/PublicMapViewer"), {
  ssr: false,
  loading: () => (
    <div className="flex h-full items-center justify-center text-[13px] text-slate-400">
      Drawing…
    </div>
  ),
});

const EXAMPLES = [
  "I sell handmade jewelry on Shopify, mostly through Instagram",
  "I'm a business coach called Northstar Coaching, selling a $497 program via webinars",
  "We run a SaaS for gyms, $49/mo with a free trial",
  "I'm a dentist and too many patients no-show their appointments",
];

type Result = {
  id: string;
  name: string;
  flow: FunnelFlow;
  editsLeft?: number;
  /** Set when the map came from the template library, not from the model. */
  degraded?: "temporary" | "capacity";
};

function toViewerMap(r: Result): PublicMap {
  const nodes = r.flow.nodes ?? [];
  return {
    name: r.name,
    updatedAt: "",
    flow: r.flow,
    nodeCount: nodes.length,
    flowCount: nodes.filter((n) => n.type === "flow").length,
  };
}

/** Free tool: describe your business → a real funnel map, claimable. */
export default function FunnelGeneratorPage() {
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [restored, setRestored] = useState(false);
  const [refineText, setRefineText] = useState("");
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState("");

  const refine = async () => {
    if (!result || refineText.trim().length < 3) return;
    setRefining(true);
    setRefineError("");
    try {
      const res = await fetch("/api/tools/refine-funnel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: result.id, instruction: refineText.trim() }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error ?? "something went wrong");
      setResult(json);
      setRefineText("");
      try {
        localStorage.setItem(
          STORE_KEY,
          JSON.stringify({ ...json, description })
        );
      } catch {
        /* nonessential */
      }
    } catch (e) {
      setRefineError(e instanceof Error ? e.message : "something went wrong");
    } finally {
      setRefining(false);
    }
  };

  // A generated map is a tiny investment — don't lose it to a bounce.
  const STORE_KEY = "frazzle:lastGeneratedFunnel";
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as Result & { description?: string };
        if (saved?.id && saved?.flow) {
          setResult(saved);
          if (saved.description) setDescription(saved.description);
          setRestored(true);
        }
      }
    } catch {
      /* corrupt/absent — fresh start */
    }
  }, []);

  const generate = async (text?: string) => {
    const value = (text ?? description).trim();
    if (value.length < 10) {
      setError("Tell us a little more — one or two sentences is perfect.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/tools/generate-funnel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description: value }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error ?? "something went wrong");
      setResult(json);
      setRestored(false);
      try {
        localStorage.setItem(
          STORE_KEY,
          JSON.stringify({ ...json, description: value })
        );
      } catch {
        /* storage full/blocked — nonessential */
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <SiteShell
      title="Free Funnel Map Generator — describe your business, get a funnel"
      description="Free funnel generator: describe your business in a sentence and get a complete, editable funnel map — pages, email sequences, waits and branches. Claim it into a free FrazzleCRM workspace and it runs for real."
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              name: "Generate a marketing funnel map for your business",
              step: [
                { "@type": "HowToStep", name: "Describe your business in one or two sentences" },
                { "@type": "HowToStep", name: "Review the generated funnel map — pages, emails, waits and branches" },
                { "@type": "HowToStep", name: "Claim the map into a free FrazzleCRM workspace and customize it" },
              ],
            }),
          }}
        />
      </Head>

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-4xl px-6 pb-10 pt-16 text-center">
          <div className="mx-auto mb-4 w-fit rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[12px] font-semibold text-indigo-700">
            Free tool — no signup to try it
          </div>
          <h1 className="text-[36px] font-extrabold leading-[1.12] tracking-tight text-slate-900 sm:text-[44px]">
            Describe your business.{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              Get your funnel.
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-[15.5px] leading-relaxed text-slate-600">
            One sentence in, a complete funnel map out — pages, email
            sequence, waits and branches, drawn and wired. Claim it and
            it&apos;s a real automation, not a picture.
          </p>

          <div className="mx-auto mt-7 max-w-2xl">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) generate();
              }}
              rows={3}
              placeholder='e.g. "I sell a $29/mo meal-planning app and most of my audience comes from TikTok"'
              className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-[14.5px] text-slate-800 shadow-sm outline-none placeholder:text-slate-400 focus:border-indigo-400"
            />
            <button
              onClick={() => generate()}
              disabled={busy}
              className="mt-3 w-full rounded-xl bg-indigo-600 px-6 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500 disabled:opacity-60 sm:w-auto sm:px-10"
            >
              {busy ? "Drawing your funnel…" : "Generate my funnel map"}
            </button>
            {error && (
              <p className="mt-2 text-[13px] font-medium text-red-500">{error}</p>
            )}
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  onClick={() => {
                    setDescription(ex);
                    void generate(ex);
                  }}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11.5px] text-slate-500 hover:border-indigo-300 hover:text-indigo-600"
                >
                  {ex.length > 46 ? `${ex.slice(0, 46)}…` : ex}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {result && (
        <section className="mx-auto max-w-6xl px-6 pb-6">
          {restored && (
            <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-[13px] text-emerald-800">
              Welcome back — your map from earlier is still here. Claim it
              before it gets lonely.
            </div>
          )}
          {result.degraded && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-900">
              <span>
                {result.degraded === "capacity"
                  ? "We’ve hit today’s limit for custom maps, so this is the closest funnel in our template library. It’s still real and claimable — check back tomorrow for one built from your description."
                  : "Our funnel architect didn’t answer just now, so this is the closest funnel in our template library rather than one built from your description."}
              </span>
              {result.degraded === "temporary" && (
                <button
                  onClick={() => void generate()}
                  disabled={busy}
                  className="min-h-[44px] shrink-0 rounded-xl border border-amber-300 bg-white px-4 py-2.5 text-[13px] font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                >
                  {busy ? "Trying…" : "Try again"}
                </button>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
            <div>
              <h2 className="text-[20px] font-bold text-slate-900">{result.name}</h2>
              <p className="text-[12.5px] text-slate-500">
                {result.flow.nodes.length} nodes · pan and zoom to explore
              </p>
            </div>
            <a
              href={`${APP_URL}/signup?claim=${result.id}`}
              className="rounded-xl bg-indigo-600 px-5 py-2.5 text-[14px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
            >
              Claim this map — free
            </a>
          </div>
          <div className="h-[480px] overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
            <PublicMapViewer map={toViewerMap(result)} />
          </div>

          {(result.editsLeft ?? 0) > 0 && (
            <div className="mx-auto mt-4 max-w-2xl">
              <div className="flex gap-2">
                <input
                  value={refineText}
                  onChange={(e) => setRefineText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void refine();
                  }}
                  placeholder='Tweak it — e.g. "make it 5 emails" or "add SMS reminders"'
                  className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[13.5px] text-slate-800 shadow-sm outline-none placeholder:text-slate-400 focus:border-indigo-400"
                />
                <button
                  onClick={() => void refine()}
                  disabled={refining}
                  className="shrink-0 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-[13.5px] font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                >
                  {refining ? "Refining…" : "Refine"}
                </button>
              </div>
              <p className="mt-1.5 text-center text-[11.5px] text-slate-400">
                {result.editsLeft} refinement{result.editsLeft === 1 ? "" : "s"} left
                — claim the map for unlimited editing.
              </p>
              {refineError && (
                <p className="mt-1 text-center text-[12px] font-medium text-red-500">
                  {refineError}
                </p>
              )}
            </div>
          )}

          <p className="mt-2 text-center text-[12px] text-slate-400">
            Claiming copies this exact map into a free workspace (5 flows,
            500 contacts, no card) — where the diagram becomes a real,
            editable automation.
          </p>
        </section>
      )}

      <section className="mx-auto max-w-4xl px-6 py-12">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          {[
            ["1. Describe", "Your product, your price, where your audience comes from — one sentence is enough."],
            ["2. Review & refine", "AI designs a funnel specific to your business — then tell it what to tweak: more emails, add SMS, change the offer."],
            ["3. Make it real", "Claim it into a free workspace. In FrazzleCRM the map IS the automation — edit either, both change."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <h3 className="text-[15px] font-bold text-slate-900">{t}</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-slate-600">{b}</p>
            </div>
          ))}
        </div>
      </section>
    </SiteShell>
  );
}
